import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Compass, ListChecks, GraduationCap, ChatCircleDots, ClipboardText } from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/28861794/pexels-photo-28861794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: Compass, title: "Framework Explorer", body: "NIST AI RMF, ISO/IEC 42001, EU AI Act — deconstructed clause by clause." },
  { icon: ListChecks, title: "3rd-Party Assessment", body: "The 9-step end-to-end vendor AI assessment used by governance teams." },
  { icon: GraduationCap, title: "Interactive Quizzes", body: "Score, review, and remember. Progress tracked across sessions." },
  { icon: ChatCircleDots, title: "AI Tutor (Claude)", body: "Ask anything about clauses, controls, and interview scenarios — live." },
  { icon: ClipboardText, title: "Playbooks & Checklists", body: "Intake, vendor DD, high-risk readiness — copy-paste ready for day one." },
  { icon: ShieldCheck, title: "Interview Q&A Bank", body: "Hand-crafted answers modelled on real AI-Governance interviews." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <header className="border-b border-border bg-white/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <div className="w-7 h-7 bg-primary text-primary-foreground flex items-center justify-center">
              <Compass size={16} weight="bold" />
            </div>
            <span className="font-display text-xl tracking-tight">DKK Info Tech</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-features">Features</a>
            <a href="#curriculum" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-curriculum">Curriculum</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing">For Teams</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm px-3 py-2 hover:text-primary transition-colors" data-testid="landing-login">Sign in</Link>
            <Link to="/register" className="text-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-[color:hsl(223_100%_28%)] transition-colors flex items-center gap-2" data-testid="landing-cta-primary">
              Start free <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-white/70" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="overline mb-6" data-testid="hero-overline">AI Risk · Governance · 3rd-Party Assessment</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              Master AI Governance.<br/>
              <span className="text-primary">Pass the interview.</span><br/>
              Own the process.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
              The definitive learning platform for the NIST AI RMF, ISO/IEC 42001, and the EU AI Act —
              plus a 9-step Third-Party AI Assessment playbook, an AI tutor, and interview drills used by real governance teams.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="px-5 py-3 bg-primary text-primary-foreground text-sm font-medium hover:bg-[color:hsl(223_100%_28%)] transition-colors flex items-center gap-2" data-testid="hero-cta-start">
                Start learning free <ArrowRight size={16} weight="bold" />
              </Link>
              <a href="#features" className="px-5 py-3 border border-foreground/80 text-sm font-medium hover:bg-foreground hover:text-white transition-colors" data-testid="hero-cta-features">
                See what's inside
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <span>NIST AI RMF 1.0</span><span>·</span>
              <span>ISO/IEC 42001</span><span>·</span>
              <span>EU AI Act 2024/1689</span>
            </div>
          </div>

          <aside className="lg:col-span-4 fade-up delay-2">
            <div className="border border-border bg-white p-6">
              <div className="overline mb-3">The 9-Step Process</div>
              <ol className="space-y-2 text-sm">
                {["Intake & Inventory","Risk Categorization","Vendor Due Diligence","Documentation Review","Technical Testing (TEVV)","Legal & Contract","Approval","Deployment & Oversight","Post-Market Monitoring"].map((s,i) => (
                  <li key={s} className="flex items-start gap-3">
                    <span className="font-mono text-xs bg-secondary px-2 py-0.5 mt-0.5">{String(i+1).padStart(2,"0")}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-14">
            <div className="md:col-span-4">
              <div className="overline mb-3">What you get</div>
              <h2 className="font-display text-4xl lg:text-5xl leading-none tracking-tighter">Everything you need. Nothing you don't.</h2>
            </div>
            <p className="md:col-span-8 md:col-start-6 text-lg text-muted-foreground leading-relaxed">
              Built by practitioners for practitioners. Six modules that mirror how a real AI-Governance function operates — from vendor intake to post-market monitoring. Study the theory, drill the interview, and deploy the playbook.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-border">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="p-8 border-r border-b border-border bg-white hover:bg-secondary/50 transition-colors" data-testid={`feature-${i}`}>
                <f.icon size={28} weight="duotone" className="text-primary mb-4" />
                <h3 className="font-display text-2xl mb-2 leading-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3">Curriculum</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter leading-[0.95] mb-6">Three frameworks. One coherent map.</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              We cross-walk NIST AI RMF's functions, ISO 42001's clauses, and the EU AI Act's tiers so you understand which control satisfies which obligation — not just what each document says in isolation.
            </p>
            <Link to="/register" className="inline-flex items-center gap-2 text-sm font-medium border-b-2 border-primary text-primary pb-1" data-testid="curriculum-cta">
              Enroll & explore <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 border-t border-l border-border">
            {[
              { k: "NIST AI RMF", v: "Govern · Map · Measure · Manage", note: "US · voluntary" },
              { k: "ISO/IEC 42001", v: "PDCA + Annex A controls", note: "Global · certifiable" },
              { k: "EU AI Act", v: "Risk tiers + GPAI", note: "EU · binding" },
            ].map((c) => (
              <div key={c.k} className="p-6 border-r border-b border-border">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{c.note}</div>
                <div className="font-display text-2xl mb-2 leading-tight">{c.k}</div>
                <div className="text-sm text-foreground">{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-accent mb-3">Onboard faster · Interview stronger</div>
            <h2 className="font-display text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              Your first 30 days as an AI Governance practitioner — codified.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-4 bg-accent text-white text-sm font-semibold hover:bg-white hover:text-foreground transition-colors" data-testid="footer-cta">
              Create free account <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-col md:flex-row justify-between text-xs text-muted-foreground font-mono uppercase tracking-widest gap-2">
          <div>© 2026 DKK Info Tech</div>
          <div>Built for practitioners · Powered by Claude Sonnet</div>
        </div>
      </footer>
    </div>
  );
}
